import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV_ITEMS = [
  { label: "Origin", href: "/story" },
  { label: "Campsites", href: "/land" },
  { label: "Tides", href: "/environment" },
  { label: "Off Grid", href: "/offgrid" },
  { label: "Activities", href: "/activities" },
  { label: "Whales", href: "/whales" },
  { label: "Photography", href: "/gallery" },
  { label: "Videos", href: "/videos" },
  { label: "FAQ", href: "/faq" },
  { label: "Guest Book", href: "/guestbook" },
  { label: "Reserve", href: "/waitlist" },
];

function DropdownItem({ item, closeMobile, solid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + "/");

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!item.children) {
    return (
      <Link to={item.href} onClick={closeMobile}
        className={`font-body text-sm tracking-wide transition-colors duration-300 hover:text-wc-teal ${isActive ? "text-wc-teal" : solid ? "text-wc-slate" : "text-white"}`}>
        {item.label}
      </Link>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)}
        className={`font-body text-sm tracking-wide transition-colors duration-300 hover:text-wc-teal flex items-center gap-1 ${isActive ? "text-wc-teal" : solid ? "text-wc-slate" : "text-white"}`}>
        {item.label} <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 py-2 bg-white rounded-md shadow-lg border border-wc-driftwood/20 min-w-[220px] z-50">
          <Link to={item.href} onClick={() => { setOpen(false); closeMobile?.(); }}
            className="block px-4 py-2 font-body text-sm text-wc-forest font-medium hover:bg-wc-cream transition-colors">
            Overview
          </Link>
          <div className="border-t border-wc-driftwood/15 my-1" />
          {item.children.map((child) => (
            <Link key={child.href} to={child.href} onClick={() => { setOpen(false); closeMobile?.(); }}
              className="block px-4 py-1.5 font-body text-sm text-wc-slate hover:text-wc-forest hover:bg-wc-cream transition-colors">
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  return (
    <nav data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${solid ? "nav-solid py-3" : "nav-transparent py-5"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between">
        <Link to="/" className="inline-flex flex-col"
          data-testid="nav-logo">
          <span className={`font-display text-lg sm:text-xl font-bold tracking-tight leading-none whitespace-nowrap ${solid ? "text-wc-forest" : "text-white"}`}>
            Whale Cove Off Grid
          </span>
          <span className={`font-accent italic text-[11px] sm:text-xs tracking-[0.12em] leading-none text-center whitespace-nowrap mt-0 ${solid ? "text-wc-slate" : "text-white/90"}`}>
            Digby Neck, Nova Scotia
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            <DropdownItem key={item.label} item={item} solid={solid} />
          ))}
        </div>

        {/* Mobile toggle */}
        <button data-testid="mobile-menu-toggle" className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen
            ? <X className={`w-6 h-6 ${solid ? "text-wc-forest" : "text-white"}`} />
            : <Menu className={`w-6 h-6 ${solid ? "text-wc-forest" : "text-white"}`} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-wc-cream/95 backdrop-blur-md border-t border-wc-driftwood">
          <div className="px-6 py-6 flex flex-col gap-3">
            {NAV_ITEMS.map((item) => (
              <div key={item.label}>
                <Link to={item.href} onClick={() => setMobileOpen(false)}
                  className="font-body text-wc-forest text-base font-medium py-1 block">
                  {item.label}
                </Link>
                {item.children && (
                  <div className="pl-4 mt-1 mb-2 space-y-1 border-l border-wc-driftwood/30">
                    {item.children.map((child) => (
                      <Link key={child.href} to={child.href} onClick={() => setMobileOpen(false)}
                        className="font-body text-wc-slate text-sm py-0.5 block">
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
